import { moveFlashcardsDb } from "../features/flashcards/review-store.ts";

/**
 * Ключи устройства после переноса заглушки (9.7 `docs/account-adopt-design.md`).
 *
 * Этот браузер подтвердил перенос, и заглушкой стал владелец ЕГО сессии (3.2,
 * строка 9, «годен один»): сервер теперь говорит, что в сессии другой
 * `userId`. Черновики домашки, очередь заметок и очередь повторений лежат под
 * старым id, и без переписи новый `userId` их не прочёл бы никогда, то есть
 * неотправленный ответ на домашку пропал бы молча. Поэтому до `adopt(me)` веб
 * переписывает свои ключи сам.
 *
 * Хранилища и их ключи (общая часть: `userId` стоит в ключе отдельным куском):
 *
 * - черновики домашки (`features/homework/drafts.ts`): `hw:…:<userId>`, id
 *   последним куском у всех трёх видов ключа;
 * - очередь заметок (`features/feedback/queue.ts`): `fb:<userId>:<noteId>`;
 * - повторения карточек (`features/flashcards/review-store.ts`): база
 *   IndexedDB `edu-flashcards-<userId>`, переписывает `moveFlashcardsDb`.
 *
 * Идентификаторы домашек и заданий переносом не меняются, поэтому переписать
 * достаточно id пользователя. Заводя новое хранилище с `userId` в ключе,
 * допиши его сюда: иначе перенос молча потеряет его содержимое.
 *
 * ⚠️ Каждый доступ в `try/catch`, и сбой переписи не мешает входу: строки
 * остаются под старым id, ровно как на втором (перевешенном) устройстве, где
 * эта цена принята (14).
 *
 * ⚠️ **Соседняя вкладка того же браузера не охраняется, цена названа.**
 * Перепись идёт ДО `adopt`, то есть и до вести `announceSessionChanged`.
 * Соседняя вкладка под старым id ещё доли секунды может писать `hw:…:<старый>`
 * или `fb:<старый>:…`, а строки, записанные ею в старую базу карточек между
 * копией и удалением, уходят вместе с базой (`versionchange` закрывает её
 * соединение, удаление проходит). Цена та же, что на втором устройстве (7.4).
 */

/** Новый ключ хранилища, либо `null`, если ключ не наш или не этого человека. */
export function rekeyedStorageKey(key: string, oldId: string, newId: string): string | null {
  if (key.startsWith("hw:") && key.endsWith(`:${oldId}`)) {
    return `${key.slice(0, key.length - oldId.length)}${newId}`;
  }
  const fb = `fb:${oldId}:`;
  if (key.startsWith(fb)) return `fb:${newId}:${key.slice(fb.length)}`;
  return null;
}

/** То, что нужно от `localStorage`. Интерфейс, чтобы тест дал своё. */
export interface ListableStore {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Переписать ключи одного хранилища. Возвращает число перенесённых.
 *
 * ⚠️ Ключи собираются списком ДО записи: запись внутри обхода по индексу
 * сдвигает нумерацию, и соседняя запись молча пропускается (тот же приём в
 * `cleanupDrafts`).
 *
 * ⚠️ Занятый новый ключ не перезаписывается, а старый тогда остаётся: из двух
 * записей одного места ни одна не теряется, а при переносе это и вовсе
 * невозможно (домашки заглушки и выжившего разные).
 */
export function rekeyStorage(store: ListableStore, oldId: string, newId: string): number {
  if (oldId === newId) return 0;
  const moves: [string, string][] = [];
  try {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key === null) continue;
      const next = rekeyedStorageKey(key, oldId, newId);
      if (next !== null) moves.push([key, next]);
    }
  } catch {
    return 0;
  }
  let moved = 0;
  for (const [from, to] of moves) {
    try {
      const value = store.getItem(from);
      if (value === null || store.getItem(to) !== null) continue;
      store.setItem(to, value);
      store.removeItem(from);
      moved += 1;
    } catch {
      /* квота или приватное окно: этот ключ остаётся под старым id */
    }
  }
  return moved;
}

/** Переписать всё, что этот браузер хранит под `oldId`, на `newId`. */
export async function rekeyLocalData(oldId: string, newId: string): Promise<void> {
  if (oldId === newId) return;
  try {
    if (typeof localStorage !== "undefined") rekeyStorage(localStorage, oldId, newId);
  } catch {
    /* доступ к `localStorage` бросает сам по себе в части приватных окон */
  }
  try {
    await moveFlashcardsDb(oldId, newId);
  } catch {
    /* IndexedDB недоступна: очередь осталась под старым именем */
  }
}
